import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Eye, ZoomIn, ZoomOut, Download, Printer, Maximize2,
  RefreshCw, AlertTriangle, CheckCircle, Loader2
} from 'lucide-react';
import { useTemplateStore } from '../store/templateStore';
import { processTemplate, extractVariables, validateTemplate } from '@goettelman/shared/engine/processor';

export function PreviewPanel() {
  const {
    content,
    previewData,
    setPreviewContent,
    setDetectedVariables,
  } = useTemplateStore();

  const [zoom, setZoom] = useState(100);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Process template when content or data changes
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      processCurrentTemplate();
    }, 300); // Debounce to avoid excessive processing

    return () => clearTimeout(debounceTimer);
  }, [content, previewData]);

  const processCurrentTemplate = async () => {
    if (!content) {
      setPreviewContent('');
      setDetectedVariables([]);
      return;
    }

    setIsProcessing(true);

    try {
      // Validate syntax first
      const syntaxErrors = validateTemplate(content);
      
      // Parse clauses from store (mock for now)
      const clauses: Record<string, string> = {
        'habendum_warranty': 'TO HAVE AND TO HOLD the above-described premises...',
        'warranty_covenants': 'The Grantor covenants with the Grantee...',
      };

      // Process the template
      const result = processTemplate(content, previewData, clauses);

      // Update state
      setErrors([
        ...syntaxErrors.map(e => e.message),
        ...result.errors.map(e => e.message),
      ]);
      setWarnings(result.warnings.map(w => w.message));
      setDetectedVariables(result.usedVariables);

      // Generate preview HTML
      const previewHtml = generatePreviewHtml(result.content);
      setPreviewContent(previewHtml);

    } catch (err: any) {
      setErrors([err.message || 'Failed to process template']);
    } finally {
      setIsProcessing(false);
    }
  };

  const generatePreviewHtml = (processedContent: string): string => {
    const paragraphs = processedContent.split('\n\n');
    
    const bodyHtml = paragraphs.map(para => {
      const trimmed = para.trim();
      if (!trimmed) return '';
      
      // Heading detection
      if (trimmed.startsWith('#')) {
        const level = trimmed.match(/^#+/)?.[0].length || 1;
        const text = trimmed.replace(/^#+\s*/, '');
        return `<h${Math.min(level, 6)} class="heading">${escapeHtml(text)}</h${Math.min(level, 6)}>`;
      }
      
      // All caps = heading
      if (trimmed === trimmed.toUpperCase() && trimmed.length < 100 && !trimmed.includes('[')) {
        return `<h2 class="heading">${escapeHtml(trimmed)}</h2>`;
      }
      
      // Missing variable highlight
      const highlighted = trimmed.replace(
        /\[([^\]]+)\]/g,
        '<span class="missing-var">[$1]</span>'
      );
      
      return `<p>${highlighted}</p>`;
    }).join('\n');

    return `<!DOCTYPE html>
<html>
<head>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: 'Times New Roman', Times, serif;
      font-size: 12pt;
      line-height: 1.6;
      color: #1a1a1a;
      padding: 1in;
      max-width: 8.5in;
      margin: 0 auto;
      background: white;
    }
    .heading {
      font-weight: bold;
      margin: 1.5em 0 0.5em;
      text-transform: uppercase;
    }
    h1.heading { font-size: 14pt; text-align: center; }
    h2.heading { font-size: 12pt; }
    h3.heading { font-size: 11pt; }
    p {
      margin: 0 0 1em;
      text-align: justify;
      text-indent: 0.5in;
    }
    p:first-of-type { text-indent: 0; }
    .missing-var {
      background: #fef3c7;
      color: #92400e;
      padding: 0 4px;
      border-radius: 2px;
      font-family: monospace;
      font-size: 10pt;
    }
    .signature-block {
      margin-top: 2em;
      page-break-inside: avoid;
    }
    .signature-line {
      border-bottom: 1px solid #333;
      width: 250px;
      margin: 2em 0 0.5em;
    }
  </style>
</head>
<body>
${bodyHtml}
</body>
</html>`;
  };

  const escapeHtml = (text: string): string => {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  };

  const handleZoomIn = () => setZoom(z => Math.min(z + 25, 200));
  const handleZoomOut = () => setZoom(z => Math.max(z - 25, 50));

  const handleDownload = () => {
    if (iframeRef.current?.contentDocument) {
      const html = iframeRef.current.contentDocument.documentElement.outerHTML;
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'preview.html';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handlePrint = () => {
    iframeRef.current?.contentWindow?.print();
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      previewRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const { previewContent } = useTemplateStore();

  return (
    <div ref={previewRef} className="h-full flex flex-col bg-primary-900">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-primary-800/50 border-b border-primary-700/50">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-accent-400" />
          <span className="text-sm font-medium text-white">Preview</span>
          
          {isProcessing && (
            <Loader2 className="w-4 h-4 text-accent-400 animate-spin ml-2" />
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Status indicators */}
          {errors.length > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 bg-error-500/20 rounded text-error-400 text-xs">
              <AlertTriangle className="w-3 h-3" />
              {errors.length} error{errors.length !== 1 ? 's' : ''}
            </div>
          )}
          
          {warnings.length > 0 && errors.length === 0 && (
            <div className="flex items-center gap-1 px-2 py-1 bg-yellow-500/20 rounded text-yellow-400 text-xs">
              <AlertTriangle className="w-3 h-3" />
              {warnings.length} warning{warnings.length !== 1 ? 's' : ''}
            </div>
          )}
          
          {errors.length === 0 && warnings.length === 0 && content && (
            <div className="flex items-center gap-1 px-2 py-1 bg-success-500/20 rounded text-success-400 text-xs">
              <CheckCircle className="w-3 h-3" />
              Valid
            </div>
          )}

          {/* Zoom controls */}
          <div className="flex items-center gap-1 ml-2 px-2 py-1 bg-primary-700/50 rounded-lg">
            <button
              onClick={handleZoomOut}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              title="Zoom out"
            >
              <ZoomOut className="w-4 h-4 text-primary-300" />
            </button>
            <span className="text-xs text-primary-300 w-12 text-center">{zoom}%</span>
            <button
              onClick={handleZoomIn}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              title="Zoom in"
            >
              <ZoomIn className="w-4 h-4 text-primary-300" />
            </button>
          </div>

          {/* Actions */}
          <button
            onClick={() => processCurrentTemplate()}
            className="p-1.5 hover:bg-white/10 rounded transition-colors"
            title="Refresh preview"
          >
            <RefreshCw className="w-4 h-4 text-primary-300" />
          </button>
          
          <button
            onClick={handlePrint}
            className="p-1.5 hover:bg-white/10 rounded transition-colors"
            title="Print"
          >
            <Printer className="w-4 h-4 text-primary-300" />
          </button>
          
          <button
            onClick={handleDownload}
            className="p-1.5 hover:bg-white/10 rounded transition-colors"
            title="Download HTML"
          >
            <Download className="w-4 h-4 text-primary-300" />
          </button>
          
          <button
            onClick={toggleFullscreen}
            className="p-1.5 hover:bg-white/10 rounded transition-colors"
            title="Fullscreen"
          >
            <Maximize2 className="w-4 h-4 text-primary-300" />
          </button>
        </div>
      </div>

      {/* Preview Content */}
      <div className="flex-1 overflow-auto bg-[#525659] p-4">
        {errors.length > 0 ? (
          <div className="max-w-lg mx-auto mt-8">
            <div className="bg-error-500/10 border border-error-500/30 rounded-lg p-4">
              <h3 className="flex items-center gap-2 text-error-400 font-medium mb-2">
                <AlertTriangle className="w-5 h-5" />
                Template Errors
              </h3>
              <ul className="space-y-1 text-sm text-error-300">
                {errors.map((error, idx) => (
                  <li key={idx}>• {error}</li>
                ))}
              </ul>
            </div>
          </div>
        ) : previewContent ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
            }}
            className="mx-auto shadow-2xl"
          >
            <iframe
              ref={iframeRef}
              srcDoc={previewContent}
              className="w-[8.5in] min-h-[11in] bg-white border-0"
              title="Document Preview"
            />
          </motion.div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-primary-400">
            <Eye className="w-16 h-16 mb-4 opacity-30" />
            <p className="text-lg">Start typing to see preview</p>
            <p className="text-sm mt-1 opacity-70">
              Use {'{{variable}}'} syntax for dynamic content
            </p>
          </div>
        )}
      </div>

      {/* Warnings panel (collapsible) */}
      {warnings.length > 0 && errors.length === 0 && (
        <div className="px-4 py-2 bg-yellow-500/10 border-t border-yellow-500/20">
          <div className="flex items-center gap-2 text-yellow-400 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">
              {warnings[0]}
              {warnings.length > 1 && ` (+${warnings.length - 1} more)`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
